

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Skeleton,
    Divider,
    Snackbar,
    Alert,
    IconButton,
    Tooltip
} from "@mui/material";
import { Edit, Delete, Save, Close } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { UiDetailSection, UiLabeledValue } from 'components/v2/record/DetailSection';

const TripSubOptionDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const [subOption, setSubOption] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchSubOptionData();
    }, [id]);

    const fetchSubOptionData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/trip-sub-options/${id}`);
            setSubOption(response.data);
        } catch (error) {
            console.error('Error fetching trip sub-option details:', error);
            setSnackbar({ open: true, message: 'Error loading sub-option details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setSubOption(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/trip-sub-options/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Trip sub-option updated successfully', severity: 'success' });
            fetchSubOptionData();
        } catch (error) {
            console.error('Error updating trip sub-option:', error);
            setSnackbar({ open: true, message: 'Error updating sub-option', severity: 'error' });
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this trip sub-option?')) {
            try {
                await axiosInstance.delete(`/trip-sub-options/${id}`);
                setSnackbar({ open: true, message: 'Trip sub-option deleted successfully', severity: 'success' });
                setTimeout(() => navigate('/trip-sub-options'), 1000);
            } catch (error) {
                console.error('Error deleting trip sub-option:', error);
                setSnackbar({ open: true, message: 'Error deleting sub-option', severity: 'error' });
            }
        }
    };

    const handleUserClick = (userId, userName) => {
        if (userId) {
            navigate(`/users/${userId}/profile`);
        } else {
            setSnackbar({
                open: true,
                message: `Unable to find user information for ${userName}.`,
                severity: 'info',
            });
        }
    };

    const capitalizeFirstLetter = (str) => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((item) => (
                    <Grid item xs={12} md={6} key={item.field}>
                        {['created_by_name', 'updated_by_name'].includes(item.field) ? (
                            <UiLabeledValue label={item.label}>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="regular" color="text">
                                    <strong>{item.label}:</strong>
                                </MDTypography>
                                <MDTypography
                                    component="span"
                                    onClick={() => {
                                        const userId = item.field === 'created_by_name' ? subOption.created_by : subOption.updated_by;
                                        handleUserClick(userId, subOption[item.field]);
                                    }}
                                    variant="button"
                                    color="info"
                                    fontWeight="medium"
                                    sx={{
                                        cursor: 'pointer',
                                        ml: 1
                                    }}
                                >
                                    {capitalizeFirstLetter(subOption[item.field] || '')}
                                </MDTypography>
                            </MDBox>
                            </UiLabeledValue>
                        ) : (
                            <InlineEditField
                                label={item.label}
                                value={subOption?.[item.field]}
                                options={item.options || null}
                                onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
                                isEditing={isEditing}
                                readOnly={item.readOnly || false}
                                isMultiSelect={item.isMultiSelect}
                                renderValue={item.renderValue || ((val) => (val ? String(val) : '-'))}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox
                mb={3}
                p={3}
                borderRadius="lg"
                sx={{
                    backgroundColor: '#ffffff',
                    boxShadow: 3
                }}
            >
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <Skeleton variant="rectangular" height={400} />
                </MDBox>
                
            </DashboardLayout>
        );
    }

    if (!subOption) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <MDTypography>Trip Sub-Option not found</MDTypography>
                </MDBox>
                
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">
                                        {subOption.name}
                                    </MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>
                                        Sub-Option Details
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {isEditing ? (
                                        <>
                                            <Tooltip title="Cancel">
                                                <IconButton
                                                    onClick={() => {
                                                        setIsEditing(false);
                                                        setUpdatedFields({});
                                                        fetchSubOptionData();
                                                    }}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Close sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Save">
                                                <IconButton
                                                    onClick={handleSaveChanges}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Save sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <Tooltip title="Edit Sub-Option">
                                            <IconButton
                                                onClick={() => setIsEditing(true)}
                                                sx={commonIconButtonStyles}
                                            >
                                                <Edit sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Delete Sub-Option">
                                        <IconButton
                                            onClick={handleDelete}
                                            sx={commonIconButtonStyles}
                                        >
                                            <Delete sx={commonIconStyles} />
                                        </IconButton>
                                    </Tooltip>
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Basic Information", [
                                    { label: "Sub-Option Name", field: "name" },
                                    { label: "Title", field: "title" },
                                    { label: "Trip Option", field: "option_id", readOnly: true, renderValue: (val) => subOption.option?.name || val },
                                    { label: "Status", field: "status", options: ["Active", "Inactive"] },
                                    { label: "Additional Price", field: "price", renderValue: (val) => `${subOption.currency} ${val}` },
                                    { label: "Currency", field: "currency", options: ["INR", "USD", "EUR", "GBP", "AUD"] },
                                    { label: "Allocation", field: "allocation" },
                                    { label: "Sold", field: "sold", readOnly: true },
                                    { label: "Available", field: "available", readOnly: true },
                                    { label: "Sequence", field: "sequence" },
                                    { label: "Description", field: "description" },
                                ])}

                                {renderSection("Audit Information", [
                                    { label: "Created By", field: "created_by_name" },
                                    { label: "Modified By", field: "updated_by_name" },
                                    { label: "Created Date", field: "created_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                    { label: "Modified Date", field: "updated_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                ])}

                                {subOption.departure_dates && subOption.departure_dates.length > 0 && (
                                    <MDBox
                                        mb={3}
                                        p={3}
                                        borderRadius="lg"
                                        sx={{
                                            backgroundColor: '#ffffff',
                                            boxShadow: 3
                                        }}
                                    >
                                        <MDTypography variant="h6" fontWeight="medium" mb={2}>Departure Dates</MDTypography>
                                        <Grid container spacing={2}>
                                            {subOption.departure_dates.map((dep) => (
                                                <Grid item xs={12} md={4} key={dep.id}>
                                                    <Card
                                                        sx={{
                                                            p: 2,
                                                            bgcolor: theme => theme.palette.grey[100],
                                                            boxShadow: 1,
                                                            borderRadius: "lg",
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() => navigate(`/departure-dates/${dep.id}`)}
                                                    >
                                                        <MDTypography variant="button" fontWeight="bold">{dep.name}</MDTypography>
                                                        <MDTypography variant="caption" color="text">Date: {formatDate(dep.departure_date, 'date')}</MDTypography>
                                                        <MDBox display="flex" justifyContent="space-between" mt={1}>
                                                            <MDTypography variant="caption" color="text">Sold: {dep.sold}</MDTypography>
                                                            <MDTypography variant="caption" color="text">Avail: {dep.available}</MDTypography>
                                                        </MDBox>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </MDBox>
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default TripSubOptionDetail;
